/** @format */
import { useEffect } from 'react';

import { ModelSearchResultsType } from '@toucan/common/services/search/constants';
import { getModelSearchResultsQueryKey } from '@toucan/common/services/search/model/query-keys';
import { Query } from '@toucan/common/services/search/types';

import { useSortByOption } from '@toucan/client/features/search/components/model-search-results/components/sort/hooks/use-sort-by-option';
import { SearchResultsVariant } from '@toucan/client/features/search/components/search-results';
import { addSearchResultsToDataLayer } from '@toucan/client/features/search/helpers/analytics';

import { useSearchParams } from '../use-search-params';
import { useModelInfiniteResults } from './hooks/use-model-infinite';
import { useModelPaginatedResults } from './hooks/use-model-paginated';

const nullSearchResults = {
    results: [],
    resultCount: 0,
    totalResults: -1,
    spellcheck: {
        suggestions: [],
    },
    sort: {},
};

export const useModelSearchResults = (
    baseQuery: Partial<Query>,
    isInfiniteScroll: boolean,
    resultsPerPage: number,
    type: ModelSearchResultsType
) => {
    const searchParams = useSearchParams();

    const sortByOption = useSortByOption(type);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call -- Legacy code: do not use as an example.
    const queryKey = getModelSearchResultsQueryKey(baseQuery, searchParams);

    const {
        data: searchResults,
        isLoading: isSearchResultsLoading,
        dataUpdatedAt: searchResultsUpdatedAt,
    } = useModelPaginatedResults(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
        queryKey,
        isInfiniteScroll,
        resultsPerPage,
        baseQuery,
        searchParams,
        type
    );

    const {
        data: infiniteSearchResults,
        isLoading: isInfiniteSearchResultsLoading,
        isFetchingNextPage: isFetchingNextInfiniteSearchResults,
        fetchNextPage,
        hasNextPage,
    } = useModelInfiniteResults(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
        queryKey,
        isInfiniteScroll,
        resultsPerPage,
        baseQuery,
        searchParams,
        type
    );

    const activeSearchResults = isInfiniteScroll
        ? infiniteSearchResults
        : searchResults;
    const hasActiveSearchResults = Boolean(activeSearchResults);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
    const analyticsTrigger = isInfiniteScroll
        ? queryKey
        : searchResultsUpdatedAt;

    useEffect(() => {
        if (!activeSearchResults) return;

        addSearchResultsToDataLayer({
            searchResults: activeSearchResults.results,
            pageType: type,
            searchFilters: searchParams.selectedFilters ?? {},
            searchVariant: SearchResultsVariant.MODEL,
            sortedBy: sortByOption.value,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [analyticsTrigger, hasActiveSearchResults]);

    if (isInfiniteScroll) {
        return {
            results: infiniteSearchResults ?? nullSearchResults,
            isLoading: isInfiniteSearchResultsLoading,
            isFetchingNextPage: isFetchingNextInfiniteSearchResults,
            fetchNextPage,
            hasNextPage,
        };
    }

    return {
        results: searchResults ?? nullSearchResults,
        isLoading: isSearchResultsLoading,
        isFetchingNextPage: false,
        hasNextPage: false,
    };
};
